/**
 * Geolocation Service
 * Detects user's country from IP and provides shipping/currency info
 * Also detects country from postal codes
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Map of European countries
const EUROPEAN_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH', 'NO',
  'UA', 'RU', 'BY', 'MD', 'RS', 'BA', 'ME', 'AL', 'MK', 'TR'
];

// Map of Americas countries
const AMERICAS_COUNTRIES = [
  'US', 'CA', 'MX', 'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC',
  'BO', 'PY', 'UY', 'GY', 'SR', 'PA', 'CR', 'NI', 'HN', 'SV',
  'GT', 'BZ', 'JM', 'CU', 'DO', 'HT', 'BS', 'BB', 'TT', 'AG'
];

// Georgia-specific code
const GEORGIA_CODE = 'GE';

// Postal code patterns for detecting countries
const POSTAL_CODE_PATTERNS = {
  GE: /^[0-9]{4}$/,  // Georgia: 4 digits (e.g., 0160)
  US: /^[0-9]{5}(-[0-9]{4})?$/,  // US: 5 digits or ZIP+4 (e.g., 10001 or 10001-0001)
  CA: /^[A-Z][0-9][A-Z]\s?[0-9][A-Z][0-9]$/i,  // Canada: A1A 1A1
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,  // UK: SW1A 1AA
  DE: /^[0-9]{5}$/,  // Germany: 5 digits
  FR: /^[0-9]{5}$/,  // France: 5 digits
  IT: /^[0-9]{5}$/,  // Italy: 5 digits
  ES: /^[0-9]{5}$/,  // Spain: 5 digits
  NL: /^[0-9]{4}\s?[A-Z]{2}$/i,  // Netherlands: 1234 AB
  BE: /^[0-9]{4}$/,  // Belgium: 4 digits
  AT: /^[0-9]{4}$/,  // Austria: 4 digits
  CH: /^[0-9]{4}$/,  // Switzerland: 4 digits
  SE: /^[0-9]{3}\s?[0-9]{2}$/,  // Sweden: 123 45
  NO: /^[0-9]{4}$/,  // Norway: 4 digits
};

export const getCountryFromIP = async () => {
  try {
    if (!API_BASE_URL) {
      console.warn('API_BASE_URL not set');
      return null;
    }

    const response = await fetch(`${API_BASE_URL}/api/geolocation`);
    if (!response.ok) {
      throw new Error(`Failed to fetch geolocation: ${response.status}`);
    }
    const data = await response.json();
    return data.countryCode || null;
  } catch (error) {
    console.error('Geolocation error:', error);
    return null;
  }
};

/**
 * Detect country code from postal code
 * Uses regex patterns to identify country by postal code format
 */
export const getCountryFromPostalCode = (postalCode) => {
  if (!postalCode) return null;

  const trimmedCode = postalCode.trim();

  // Check each country's postal code pattern
  for (const [countryCode, pattern] of Object.entries(POSTAL_CODE_PATTERNS)) {
    if (pattern.test(trimmedCode)) {
      return countryCode;
    }
  }

  return null;
};

export const getDefaultCurrency = (countryCode) => {
  if (!countryCode) return 'USD'; // Default fallback

  if (countryCode === GEORGIA_CODE) {
    return 'GEL';
  }

  if (EUROPEAN_COUNTRIES.includes(countryCode)) {
    return 'EUR';
  }

  if (AMERICAS_COUNTRIES.includes(countryCode)) {
    return 'USD';
  }

  // Default to USD for other regions
  return 'USD';
};

export const getShippingCost = (countryCode, subtotal) => {
  if (!countryCode) {
    // Default to international shipping
    return {
      cost: 50,
      currency: 'EUR',
      description: 'International Shipping'
    };
  }

  // Free shipping in Georgia
  if (countryCode === GEORGIA_CODE) {
    return {
      cost: 0,
      currency: 'GEL',
      description: 'Free Shipping in Georgia'
    };
  }

  // Europe: $50 EUR
  if (EUROPEAN_COUNTRIES.includes(countryCode)) {
    return {
      cost: 50,
      currency: 'EUR',
      description: 'International Shipping (Europe)'
    };
  }

  // Americas: $50 USD
  if (AMERICAS_COUNTRIES.includes(countryCode)) {
    return {
      cost: 50,
      currency: 'USD',
      description: 'International Shipping (Americas)'
    };
  }

  // Other regions: $50 USD
  return {
    cost: 50,
    currency: 'USD',
    description: 'International Shipping'
  };
};

export const getCountryInfo = async () => {
  try {
    const countryCode = await getCountryFromIP();
    const currency = getDefaultCurrency(countryCode);
    const shipping = getShippingCost(countryCode, 0);

    return {
      countryCode,
      currency,
      shipping
    };
  } catch (error) {
    console.error('Error getting country info:', error);
    return {
      countryCode: null,
      currency: 'USD',
      shipping: {
        cost: 50,
        currency: 'USD',
        description: 'International Shipping'
      }
    };
  }
};
